import { TimeRange } from 'bindings/reports/generic/TimeRange';
import _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import Switch from '../../../components_v2/Switch/Switch';
import { GenericReport } from 'bindings/reports/generic/GenericReport';
import { CalculatedField, CalculatedFieldTarget } from '../../../atoms/calculatedFields';
import { AUsers } from '../../../atoms/global/users';
import { AProducts } from '../../../atoms/product';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import Input from '../../../components_v2/input/Input';
import Popup from '../../../components_v2/popup/Popup';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import { OnDateChangeType, RawDatePicker } from '../../../components_v2/toolbarFilter/time/DatePicker';
import { momentToNaiveDateTime } from '../../../components_v2/utils';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { PrimaryColor, BorderColor, DarkGreySidely2, RedSidely, SidelyBlack, SurfaceColor } from '../../../styles/global/css/Utils';
import { Translate, translateToNode, translateToString } from '../../../styles/global/translate';
import { TitleAndChild } from '../../client-companies/popup/Detail';
import { LineHoverDiv } from '../../client-companies/popup/DetailDisplayer';
import { FlexDiv } from '../../products/style';
import { getFinalyFilteredProducts, initFilters, ReportFiltersContext } from '../../reports/generic/generic';
import { getExternalDatasets } from '../../reports/generic/action';
import { BeginPeriods, EndPeriods, GenericEditorContext, useSetFilters } from '../../reports/generic/genericEditor';
import { Editor } from '../../reports/generic/genericEditorRecursive';
import AdvancedFilters from '../../../components_v2/filter/AdvancedFilters';
import { FilterResult, FilterTree, filterTreeLength } from '../../../components_v2/filter/model/Model';
import { reverseConvertFilters } from '../../../components_v2/filter/pages/FilterList';
import type { FilterParameter } from '../../../components_v2/filter/pages/FilterList';
import { generatePromotionFilters } from '../../../components_v2/filter/promotionFilters';
import { getCompanyFilters } from '../../client-companies/data/action';

const CalcFieldTitle = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	color: ${SidelyBlack};
	gap: 15px;
	padding: 0 10px 10px 10px;
	height: 50px;
	font-size: 16px;
	font-weight: 600;
	justify-content: space-between;
	&::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 4%;
		width: 92%;
		border-bottom: 2px solid ${BorderColor};
	}
`;

const OptionsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 15px;
	width: 50%;
	height: calc(100% - 50px);
	padding: 20px 15px 0 0;
	border-right: 2px solid ${BorderColor};
	overflow-y: scroll;
	overflow-x: hidden;
`;

const SaveButton = styled(DefaultButton)`
	width: 7vw;
	margin: 5px;
`;

const FiltersWrapper = styled(LineHoverDiv)`
	display: flex;
	flex-direction: column;
	gap: 15px;
	padding-left: 20px;
	width: 97%;
`;

const MandatoryField = styled.p`
	color: ${DarkGreySidely2};
	font-size: 13px;
    &:before {
		color: ${RedSidely};
		margin-right: 3px;
        content: "*";
    }
`;

const BoolSliderContainer = styled.div<{ isActive?: boolean, activeColor?: string, opacity?: string, cursor?: string, warningMessage?: string, width?: string }>`
	position: relative;
	display: flex;
	align-items: center;
	--container-width: ${props => props.width ?? '38px'};
	width: var(--container-width);
	padding-left: calc(var(--container-width) * 0.1);
	background-color: ${props => props.isActive ? props.activeColor ?? PrimaryColor : DarkGreySidely2};
	opacity: ${props => props.opacity ?? '1'};
	aspect-ratio: 38 / 21;
	border-radius: calc(var(--container-width) * 0.7);
	transition: background-color 0.3s;
	cursor: ${props => props.cursor ?? 'pointer'};
	&:hover {
		&::after {
			display: ${props => props.cursor === 'not-allowed' ? 'block' : 'none'};
			content: ${props => props.warningMessage ? `'${props.warningMessage}'` : ''};
			position: absolute;
			z-index: 10;
			width: 21vw;
			top: 30px;
			padding: 5px;
			background-color: ${BorderColor};
			border-radius: 5px;
			font-size: 12px;
		}
	}
`;

const BoolSliderDot = styled.div<{ isActive?: boolean }>`
	width: calc(var(--container-width) * 0.38);
	aspect-ratio: 1 / 1;
	background-color: ${SurfaceColor};
	border-radius: 50%;
	transform: translateX(${props => props.isActive ? 'calc(var(--container-width) * 0.42)' : '0'});
	transition: transform 0.3s;
`;

export function BoolSlider<T = unknown>(props: { isActive: boolean, onClick?: (value: T) => void | undefined, activeColor?: string, opacity?: string, cursor?: string, width?: string }): JSX.Element {
	return <>
		<BoolSliderContainer
			onClick={() => props.onClick && props.onClick(undefined as unknown as T)}
			isActive={props.isActive}
			activeColor={props.activeColor}
			opacity={props.opacity}
			cursor={props.cursor}
			warningMessage={props.cursor === 'not-allowed' ? translateToString('objectives.waring_unset') : undefined}
			width={props.width}
		>
			<BoolSliderDot isActive={props.isActive} />
		</BoolSliderContainer>
	</>;
}

export function CalculatedFieldsCreation(props: {
	isOpen: boolean,
	setIsOpen: (value: boolean) => void,
	setCalculatedField: (value: CalculatedField | null) => void,
	calculatedField: CalculatedField | null,
	// created from the promotion fields page: one value per promotion (or per
	// promotion x product with the per-product toggle)
	promotion?: boolean,
}) {
	const { isOpen, setIsOpen, setCalculatedField, calculatedField } = props;
	const { report, setReport } = React.useContext(GenericEditorContext);
	const [calcFieldName, setCalcFieldName] = React.useState<string>(calculatedField?.field_name || '');
	// one value per promotion (from the forms filled for it) instead of per company
	const [perPromotion, setPerPromotion] = React.useState<boolean>(
		!!props.promotion || calculatedField?.target === CalculatedFieldTarget.Promotion || calculatedField?.target === CalculatedFieldTarget.PromotionProduct
	);
	const [perProduct, setPerProduct] = React.useState<boolean>(
		calculatedField?.target === CalculatedFieldTarget.CompanyProduct
		|| calculatedField?.target === CalculatedFieldTarget.PromotionProduct
		|| !!(calculatedField?.report as unknown as { external?: { per_product?: boolean } })?.external?.per_product
	);
	// un champ externe (ClickHouse) n'a pas de rapport: sa définition
	// {"external": ...} porte la mesure, la fenêtre glissante et ses options
	// a scorecard and an objective field have no panels either, and this
	// heuristic would file them as external KPIs — saving would then rewrite
	// their definition as a dataset query
	const isExternalField = calculatedField?.target !== CalculatedFieldTarget.Score
		&& calculatedField?.target !== CalculatedFieldTarget.Objective
		&& (calculatedField?.target === CalculatedFieldTarget.External || (!!calculatedField?.report && !calculatedField.report.panels));
	const externalDef = (calculatedField?.report as unknown as { external?: { dataset: string, measure: string, window_weeks?: number, begin?: string, end?: string, vs_n1?: boolean, minor_units?: boolean, per_product?: boolean } })?.external;
	// en création, choisir une mesure externe dans le picker bascule le champ
	// en KPI externe (le moteur classique ne sait pas les calculer)
	const externalPick = (report?.panels?.[0]?.columns?.[0] as unknown as { External?: { dataset: string, measure: string } } | undefined)?.External;
	const isExternalMode = isExternalField || (!calculatedField && !!externalPick);
	const [windowWeeks, setWindowWeeks] = React.useState<number>(externalDef?.window_weeks ?? 12);
	// fenêtre glissante (semaines depuis aujourd'hui) ou période fixe [begin, end]
	const [windowIsRolling, setWindowIsRolling] = React.useState<boolean>(!externalDef?.begin);
	const [vsN1, setVsN1] = React.useState<boolean>(externalDef?.vs_n1 ?? false);
	const trimmedCalcFieldName = calcFieldName.trim();
	const [dynamicDateRange, setDynamicDateRange] = React.useState<TimeRange>(calculatedField?.report?.range ? {
		begin: calculatedField.report.range.begin,
		end: calculatedField.report.range.end
	} : { begin: '90DaysAgo', end: 'Today' });
	const [staticDateRange, setStaticDateRange] = React.useState<OnDateChangeType>(calculatedField?.report?.range ? {
		startDate: moment(calculatedField.report.range.begin),
		endDate: moment(calculatedField.report.range.end)
	} : { startDate: moment().subtract(3, 'month').startOf('day'), endDate: moment().startOf('day') });
	const { filters, jsxFilters } = React.useContext(ReportFiltersContext);
	const owners = useRecoilValue(AUsers);
	const [columnSelected, setColumnSelected] = React.useState<boolean>(false);
	const [defaultFilters, setDefaultFilters] = React.useState<string | null>(null);
	const products = useRecoilValue(AProducts);
	const [periodIsDynamic, setPeriodIsDynamic] = React.useState<boolean>(false);
	// the report-level company filter of the definition (promotions included):
	// the recompute worker only computes values for matching companies
	const [companyFilterOpen, setCompanyFilterOpen] = React.useState<boolean>(false);
	const [companyFilterParams, setCompanyFilterParams] = React.useState<FilterParameter[]>([]);
	const [filterResult, setFilterResult] = React.useState<FilterResult | undefined>(undefined);
	const [initialFilterTree, setInitialFilterTree] = React.useState<string>('null');
	React.useEffect(() => {
		getCompanyFilters()
			.then(async response => setCompanyFilterParams([...response.filters, await generatePromotionFilters()]))
			.catch(console.error);
	}, []);
	const BeginPeriodTypes: DropdownData[] = [];
	const EndPeriodTypes: DropdownData[] = [];

	BeginPeriodTypes.push({ value: undefined, label: translateToString('report_editor.period.begin_default') });
	EndPeriodTypes.push({ value: undefined, label: translateToString('report_editor.period.end_default') });

	BeginPeriods.map(e => (BeginPeriodTypes.push({ value: e, label: translateToString('report_editor.period.' + e) })));
	EndPeriods.map(e => (EndPeriodTypes.push({ value: e, label: translateToString('report_editor.period.' + e) })));


	React.useEffect(() => {
		if (calculatedField?.report?.panels) {
			const { filters } = initFilters(calculatedField.report);
			setDefaultFilters(JSON.stringify(filters));
		}
	}, [calculatedField]);

	function hasChangesCreate(): boolean {
		return columnSelected && trimmedCalcFieldName.length > 0 && ((dynamicDateRange.begin !== null && dynamicDateRange.end !== null) || (staticDateRange.startDate !== null && staticDateRange.endDate !== null));
	}

	function hasChangesEdit(): boolean {
		if (!calculatedField || trimmedCalcFieldName.length <= 0) return false;
		const filtersForComparison = _.cloneDeep(filters);
		if (filtersForComparison?.my_filters?.products?.all === true) {
			filtersForComparison.my_filters = { 'products': { 'all': true, 'products': [] } };
		}
		const basicChanges = columnSelected || calculatedField.field_name !== trimmedCalcFieldName || defaultFilters !== JSON.stringify(filtersForComparison)
			|| initialFilterTree !== JSON.stringify(filterResult?.formatted ?? null);
		if (periodIsDynamic && dynamicDateRange.begin !== null && dynamicDateRange.end !== null) {
			return basicChanges || (
				calculatedField.report.range?.begin !== dynamicDateRange.begin || calculatedField.report.range?.end !== dynamicDateRange.end
			);
		}
		else if (staticDateRange.startDate !== null && staticDateRange.endDate !== null) {
			return basicChanges || (
				calculatedField.report.range?.begin !== momentToNaiveDateTime(staticDateRange.startDate) || calculatedField.report.range?.end !== momentToNaiveDateTime(staticDateRange.endDate)
			);
		}
		return false;
	}

	const externalTimeOk = windowIsRolling ? windowWeeks > 0 : !!(staticDateRange.startDate && staticDateRange.endDate);
	const unlockSave = isExternalField
		? trimmedCalcFieldName.length > 0 && externalTimeOk
		: isExternalMode ? columnSelected && trimmedCalcFieldName.length > 0 && externalTimeOk
			: calculatedField ? hasChangesEdit() : hasChangesCreate();

	React.useEffect(() => {
		if (isOpen) {
			if (calculatedField && calculatedField.report) {
				setReport(calculatedField.report);
				setCalcFieldName(calculatedField.field_name);
				const storedFilter = (calculatedField.report as unknown as { filter?: FilterTree | FilterResult | null }).filter;
				if (storedFilter && 'formatted' in storedFilter) {
					setFilterResult(storedFilter as FilterResult);
					setInitialFilterTree(JSON.stringify((storedFilter as FilterResult).formatted ?? null));
				} else if (storedFilter) {
					setFilterResult({ values: reverseConvertFilters(storedFilter as FilterTree), formatted: storedFilter as FilterTree });
					setInitialFilterTree(JSON.stringify(storedFilter));
				} else {
					setFilterResult(undefined);
					setInitialFilterTree('null');
				}
				const external = (calculatedField.report as unknown as { external?: { window_weeks?: number, begin?: string, end?: string, vs_n1?: boolean, per_product?: boolean } })?.external;
				setPerProduct(calculatedField.target === CalculatedFieldTarget.CompanyProduct || calculatedField.target === CalculatedFieldTarget.PromotionProduct || !!external?.per_product);
				setPerPromotion(!!props.promotion || calculatedField.target === CalculatedFieldTarget.Promotion || calculatedField.target === CalculatedFieldTarget.PromotionProduct);
				setWindowWeeks(external?.window_weeks ?? 12);
				setWindowIsRolling(!external?.begin);
				setVsN1(external?.vs_n1 ?? false);
				if (external?.begin && external?.end) {
					setStaticDateRange({ startDate: moment(external.begin), endDate: moment(external.end) });
				}
				if (calculatedField.report.range) {
					if (moment(calculatedField.report.range.begin).isValid() && moment(calculatedField.report.range.end).isValid()) {
						setPeriodIsDynamic(false);
						setStaticDateRange({
							startDate: moment(calculatedField.report.range.begin),
							endDate: moment(calculatedField.report.range.end)
						});
						setDynamicDateRange({ begin: '90DaysAgo', end: 'Today' });
					}
					else {
						setPeriodIsDynamic(true);
						setDynamicDateRange({
							begin: calculatedField.report.range.begin,
							end: calculatedField.report.range.end
						});
						setStaticDateRange({ startDate: moment().subtract(3, 'month').startOf('day'), endDate: moment().startOf('day') });
					}
				}
			}
			else if (!calculatedField) {
				setCalcFieldName('');
				setPerProduct(false);
				setPerPromotion(!!props.promotion);
				setFilterResult(undefined);
				setInitialFilterTree('null');
				setDynamicDateRange({ begin: '90DaysAgo', end: 'Today' });
				setStaticDateRange({ startDate: moment().subtract(3, 'month').startOf('day'), endDate: moment().startOf('day') });
				setReport({
					'group_by': 'Company',
					'panels': [
						{ 'columns': [], products: undefined, name: '' }
					],
					'range': { begin: staticDateRange.startDate ? momentToNaiveDateTime(staticDateRange.startDate) : '', end: staticDateRange.endDate ? momentToNaiveDateTime(staticDateRange.endDate) : '' },
					'window': null,
					'evolution': null,
					'variation': null,
					'filter': null
				});
			}
		}
	}, [isOpen]);

	useSetFilters(setReport, report, filters, owners, filterResult);

	return <>
		<Popup isOpen={isOpen} hasBackground disableOutClick onClickOut={() => setIsOpen(false)}
			popupStyle={{ top: '0', left: '40vw', height: '100vh', width: '60vw', animate: true, boxShadow: `0px 0px 16px color-mix(in oklab, ${SidelyBlack} 6%, transparent)` }}
			content={<>
				<CalcFieldTitle>
					<div style={{ padding: '0 0 0 15px' }}><Translate id={calculatedField ? 'calculated_fields.edit' : 'calculated_fields.create'} /></div>
					<FlexDiv >
						<SaveButton
							buttonStyle={ButtonStyle.White}
							onClick={() => {
								setCalculatedField(null);
								setCalcFieldName('');
								setColumnSelected(false);
								setIsOpen(false);
							}}>
							{translateToNode('cancel')}
						</SaveButton>
						<SaveButton
							disabled={!unlockSave}
							onClick={async() => {
								setCalcFieldName('');
								setColumnSelected(false);
								if (isExternalMode) {
									// KPI externe: la définition {"external": ...} remplace le
									// rapport, le worker ClickHouse la calcule
									const base = externalDef ?? externalPick;
									// une définition sans dataset/mesure serait stockée puis
									// jamais calculée: mieux vaut bloquer ici
									if (!base?.dataset || !base?.measure) {

										console.error('external calculated field without dataset/measure', base, report);
										return;
									}
									let minorUnits = externalDef?.minor_units;
									if (minorUnits === undefined) {
										const datasets: { dataset: string, measures: { slug: string, value_type: string }[] }[] = await getExternalDatasets().catch(() => []);
										minorUnits = datasets.find(d => d.dataset === base.dataset)?.measures.find(m => m.slug === base.measure)?.value_type === 'money';
									}
									setCalculatedField({
										id: (calculatedField ? calculatedField.id : { unsync: Date.now() }) as CalculatedField['id'],
										target: CalculatedFieldTarget.External,
										field_name: trimmedCalcFieldName,
										ishidded: calculatedField?.ishidded ?? false,
										display_options: calculatedField?.display_options,
										report: {
											external: {
												dataset: base.dataset,
												measure: base.measure,
												...(windowIsRolling
													? { window_weeks: windowWeeks }
													: { begin: momentToNaiveDateTime(staticDateRange.startDate!), end: momentToNaiveDateTime(staticDateRange.endDate!) }),
												vs_n1: vsN1,
												minor_units: minorUnits,
												per_product: perProduct,
											},
										} as unknown as GenericReport
									});
									setIsOpen(false);
									return;
								}
								const finallyFilteredProducts = getFinalyFilteredProducts(filters, products);
								if (finallyFilteredProducts)
									report.product_filter = finallyFilteredProducts;
								if (periodIsDynamic && dynamicDateRange.begin && dynamicDateRange.end)
									report.range = { begin: dynamicDateRange.begin, end: dynamicDateRange.end };
								else if (staticDateRange.startDate && staticDateRange.endDate)
									report.range = { begin: momentToNaiveDateTime(staticDateRange.startDate), end: momentToNaiveDateTime(staticDateRange.endDate) };
								// the definition stores the bare filter tree: the editor state
								// (FilterResult) would not deserialize backend-side
								const reportFilter = report.filter as unknown as FilterResult | FilterTree | null | undefined;
								report.filter = ((reportFilter && 'formatted' in reportFilter ? reportFilter.formatted : reportFilter) ?? null) as unknown as typeof report.filter;
								setCalculatedField({
									id: calculatedField ? calculatedField.id : { unsync: Date.now() },
									target: perPromotion
										? (perProduct ? CalculatedFieldTarget.PromotionProduct : CalculatedFieldTarget.Promotion)
										: (perProduct ? CalculatedFieldTarget.CompanyProduct : CalculatedFieldTarget.Company),
									field_name: trimmedCalcFieldName,
									report: report,
									display_options: calculatedField?.display_options,
								});
								setIsOpen(false);
							}}>
							{translateToNode('save')}
						</SaveButton>
					</FlexDiv>
				</CalcFieldTitle>
				<FlexDiv width='100%' height='100%' align='stretch' padding='0px 10px 20px 10px'>
					<OptionsWrapper>
						<FlexDiv align='stretch' flow='column' gap='10px' padding='0 0 0 15px'>
							<FlexDiv flow='column' align='stretch' gap='8px'>
								<MandatoryField><Translate id='calculated_fields.field_name' /></MandatoryField>
								<Input
									name={'calc_field_name'}
									type={'text'}
									value={calcFieldName}
									onChange={(value) => setCalcFieldName(value)}
									inputStyle={{
										width: '25vw',
										padding: '0 0 0 10px',
										borderRadius: '5px'
									}} />
							</FlexDiv>
							{isExternalField && externalDef && <FlexDiv gap='6px'>{externalDef.dataset} · {externalDef.measure}</FlexDiv>}
							<FlexDiv gap='10px'>
								<Switch value={perProduct} onChange={setPerProduct} />
								<Translate id='calculated_fields.per_product' />
							</FlexDiv>
							{props.promotion && <span style={{ fontSize: '12px', color: DarkGreySidely2 }}><Translate id='calculated_fields.promotion_hint' /></span>}
							{isExternalMode && <FlexDiv flow='column' align='stretch' gap='8px'>
								<MandatoryField><Translate id='calculated_fields.field_period' /></MandatoryField>
								<FlexDiv justify='center' gap='12px'>
									<Translate id='report_editor.period.static_period' />
									<BoolSlider isActive={windowIsRolling} onClick={() => setWindowIsRolling(!windowIsRolling)} />
									<Translate id='calculated_fields.window_weeks' />
								</FlexDiv>
								{windowIsRolling ? <>
									<Input
										name='window_weeks'
										type='number'
										value={windowWeeks.toString()}
										onChange={(value) => setWindowWeeks(Math.min(260, Math.max(1, Number(value) || 0)))}
										inputStyle={{ width: '25vw', padding: '0 0 0 10px', borderRadius: '5px' }}
									/>
									{/* la fenêtre glisse depuis aujourd'hui: montrer la date
									    évite de compter les semaines de tête */}
									<span style={{ fontSize: '12px', color: DarkGreySidely2 }}>
										≈ {translateToString('calculated_fields.window_since')} {moment().subtract(windowWeeks, 'weeks').format('DD/MM/YYYY')}
									</span>
								</> :
									<RawDatePicker future={true} start={staticDateRange.startDate} end={staticDateRange.endDate} onDatesChange={(dates) => setStaticDateRange(dates)} fillWidth vertical />
								}
							</FlexDiv>}
							{isExternalMode && <FlexDiv gap='10px'>
								<Switch value={vsN1} onChange={setVsN1} />
								<span>vs N-1</span>
							</FlexDiv>}
							{!isExternalMode && <FlexDiv flow='column' align='stretch' gap='12px'>
								<MandatoryField><Translate id='calculated_fields.field_period' /></MandatoryField>
								<FlexDiv justify='center' gap='12px'>
									<Translate id='report_editor.period.static_period' />
									<BoolSlider isActive={periodIsDynamic} onClick={() => setPeriodIsDynamic(!periodIsDynamic)} />
									<Translate id='report_editor.period.dynamic_period' />
								</FlexDiv>
								{periodIsDynamic ?
									<Dropdown
										name='range'
										dropdownStyle={{ width: '25vw', optionWidth: '25vw' }}
										readOnly
										datalist={BeginPeriodTypes}
										selectedValue={BeginPeriodTypes.find(a => {
											if (dynamicDateRange && dynamicDateRange.begin !== '90DaysAgo') {
												return a.value === dynamicDateRange.begin;
											} else {
												return a.value === undefined;
											}
										})
										}
										onChange={value => {
											setDynamicDateRange({ ...dynamicDateRange, begin: value.value ?? '90DaysAgo', end: 'Today' });
										}}
									/>
									:
									<RawDatePicker future={true} start={staticDateRange.startDate} end={staticDateRange.endDate} onDatesChange={(dates) => setStaticDateRange(dates)} fillWidth vertical />
								}
							</FlexDiv>}
						</FlexDiv>
						{!isExternalMode && <TitleAndChild
							title={translateToString('calculated_fields.data_filters')}
							margin='0 0 0 0'
							addDiv
							defaultOpen={true}
							localCollapseStyle={{ justifyContent: 'left' }}>
							<FiltersWrapper>
								<FlexDiv flow='column' align='stretch' gap='8px'>
									<Translate id='report_editor.filter_categories.form_instances' />
									{jsxFilters && jsxFilters[0] && jsxFilters[0][1].child}
								</FlexDiv>
								<FlexDiv flow='column' align='stretch' gap='8px'>
									<Translate id='report_editor.filter_categories.form_models' />
									{jsxFilters && jsxFilters[1] && jsxFilters[1][1].child}
								</FlexDiv>
								<FlexDiv flow='column' align='stretch' gap='8px'>
									<Translate id='report_editor.filter_categories.form_creator' />
									{jsxFilters && jsxFilters[1] && jsxFilters[1][0].child}
								</FlexDiv>
								<FlexDiv flow='column' align='stretch'>
									<div style={{ padding: '0 0 8px 0' }}><Translate id='report_editor.filter_categories.product_categories' /></div>
									{jsxFilters && jsxFilters[2] && jsxFilters[2][0].child}
								</FlexDiv>
								<FlexDiv flow='column' align='stretch' gap='8px'>
									<Translate id='report_editor.filter_categories.brands' />
									{jsxFilters && jsxFilters[2] && jsxFilters[2][1].child}
								</FlexDiv>
								<FlexDiv flow='column' align='stretch' gap='8px'>
									<Translate id='report_editor.filter_categories.product_tags' />
									{jsxFilters && jsxFilters[2] && jsxFilters[2][2].child}
								</FlexDiv>
								<FlexDiv flow='column' align='stretch' gap='8px'>
									<Translate id='global.filter.company_filters' />
									<DefaultButton margin='0' onClick={() => setCompanyFilterOpen(true)}>
										<Translate id='global.filter.company_filters' /> ({filterTreeLength(filterResult?.formatted)})
									</DefaultButton>
								</FlexDiv>
							</FiltersWrapper>
						</TitleAndChild>}
					</OptionsWrapper>
					{!isExternalField && <div style={{ width: '50%', height: '97%', paddingTop: '10px' }}>
						<Editor
							column={calculatedField?.report?.panels?.[0]?.columns?.[0]}
							path={'panels[0].columns'}
							index={0}
							onSave={() => { setColumnSelected(true); }}
							isCalcField
						/>
					</div>}
				</FlexDiv>
			</>} />
		{!isExternalMode && <AdvancedFilters
			permission='ReadCompany'
			isOpen={companyFilterOpen}
			setOpen={setCompanyFilterOpen}
			filterList={companyFilterParams}
			filterValues={filterResult?.values}
			onChange={setFilterResult}
		/>}
	</>;
}
